/**
 * The Desk — portfolio metrics.
 *
 * Every figure here is derived from real records. Where the underlying data does
 * not exist, the value is `null` and the UI renders an em dash — we never
 * substitute an estimate or a placeholder number.
 *
 * Field notes (verified against live records, not assumed):
 *   contracts.total_value      — populated, basis for AUM
 *   contracts.guaranteed_value — populated (NOT `guaranteed_money`, which is empty)
 *   contracts.commission_rate  — EMPTY in practice, so agency fees are not derivable here
 */

import type { Contract, Deal, Client } from '../types'
import { money, pct } from './format'
import { getDealProbability } from './pipelineCalculations'

export type StageRow = {
  stage: string
  count: number
  gross: number
  /** Average probability across deals in the stage, 0–100. null when unset. */
  probability: number | null
  weighted: number
}

export type Holding = {
  clientId: string
  name: string
  value: number
  /** Share of AUM, 0–100 */
  share: number
  /** Latest contract end year across this client's contracts */
  through: number | null
}

export type DeskMetrics = {
  /** Sum of contract total_value */
  aum: number
  guaranteed: number | null
  /** guaranteed / aum as 0–100 */
  guaranteedPct: number | null
  /** Open = deals not Executed/Lost */
  openPipeline: number
  openCount: number
  weightedPipeline: number
  /** weighted / openPipeline as 0–100 */
  weightedPct: number | null
  /** null — requires commission data the agency has not recorded */
  feesYtd: number | null
  /** null — requires invoice/payment schedule data */
  cashNext90: number | null
  stages: StageRow[]
  holdings: Holding[]
  concentration: {
    topThreePct: number | null
    topLeague: { league: string; pct: number } | null
    weightedAvgYearsRemaining: number | null
    expiringWithin12Months: number
  }
}

const CLOSED_STAGES = new Set(['Executed', 'Lost'])

/** Stage display order matching the pipeline. */
const STAGE_ORDER = ['Prospect', 'Outreach', 'Negotiating', 'Agreed', 'Contract Sent', 'Executed', 'Lost']

function num(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

export function computeDeskMetrics(
  contracts: Contract[],
  deals: Deal[],
  clients: Client[],
  /** Fees earned this year from the commission ledger; null while unloaded or when the ledger has no rows this year. */
  feesYtd: number | null = null,
): DeskMetrics {
  // ── Portfolio ────────────────────────────────────────────────────────
  const aum = contracts.reduce((s, c) => s + num((c as any).total_value), 0)

  const gtdValues = contracts.filter(c => num((c as any).guaranteed_value) > 0)
  const guaranteed = gtdValues.length
    ? gtdValues.reduce((s, c) => s + num((c as any).guaranteed_value), 0)
    : null
  const guaranteedPct = guaranteed != null && aum > 0 ? (guaranteed / aum) * 100 : null

  // ── Pipeline ─────────────────────────────────────────────────────────
  const open = deals.filter(d => !CLOSED_STAGES.has(String(d.stage)))
  const openPipeline = open.reduce((s, d) => s + num(d.value), 0)
  const weightedPipeline = open.reduce(
    (s, d) => s + num(d.value) * (getDealProbability(d) / 100),
    0,
  )
  const weightedPct = openPipeline > 0 ? (weightedPipeline / openPipeline) * 100 : null

  const byStage = new Map<string, Deal[]>()
  for (const d of deals) {
    const k = String(d.stage || 'Unknown')
    byStage.set(k, [...(byStage.get(k) ?? []), d])
  }
  const stages: StageRow[] = [...byStage.entries()]
    .map(([stage, ds]) => {
      // Through getDealProbability, not the raw field: a settled deal keeps
      // whatever probability it carried while it was still in play, and the
      // app never revises it. This table read Executed 2 $5.0M at 40% and
      // Lost 1 $2.5M at 40%, so it discounted money already won and kept
      // money that will never arrive.
      const probs = ds.map(getDealProbability).filter(p => p > 0)
      return {
        stage,
        count: ds.length,
        gross: ds.reduce((s, d) => s + num(d.value), 0),
        probability: probs.length ? probs.reduce((s, p) => s + p, 0) / probs.length : null,
        weighted: ds.reduce((s, d) => s + num(d.value) * (getDealProbability(d) / 100), 0),
      }
    })
    .sort((a, b) => {
      const ai = STAGE_ORDER.indexOf(a.stage)
      const bi = STAGE_ORDER.indexOf(b.stage)
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
    })

  // ── Holdings (contract value per client) ─────────────────────────────
  const nameById = new Map(clients.map(c => [c.id, (c as any).full_name || 'Unknown']))
  const perClient = new Map<string, { value: number; endYear: number | null }>()
  for (const c of contracts) {
    const cid = (c as any).client_id
    if (!cid) continue
    const prev = perClient.get(cid) ?? { value: 0, endYear: null }
    const end = (c as any).end_date ? new Date((c as any).end_date).getFullYear() : null
    perClient.set(cid, {
      value: prev.value + num((c as any).total_value),
      endYear: end != null ? Math.max(prev.endYear ?? 0, end) || end : prev.endYear,
    })
  }
  const holdings: Holding[] = [...perClient.entries()]
    .map(([clientId, v]) => ({
      clientId,
      name: nameById.get(clientId) ?? 'Unknown',
      value: v.value,
      share: aum > 0 ? (v.value / aum) * 100 : 0,
      through: v.endYear,
    }))
    .sort((a, b) => b.value - a.value)

  // ── Concentration ────────────────────────────────────────────────────
  const topThreePct = holdings.length
    ? holdings.slice(0, 3).reduce((s, h) => s + h.share, 0)
    : null

  const leagueTotals = new Map<string, number>()
  for (const c of contracts) {
    const lg = (c as any).league
    if (!lg) continue
    leagueTotals.set(lg, (leagueTotals.get(lg) ?? 0) + num((c as any).total_value))
  }
  let topLeague: { league: string; pct: number } | null = null
  if (leagueTotals.size && aum > 0) {
    const [league, val] = [...leagueTotals.entries()].sort((a, b) => b[1] - a[1])[0]
    topLeague = { league, pct: (val / aum) * 100 }
  }

  const now = new Date()
  const withEnd = contracts.filter(c => (c as any).end_date)
  const weightedAvgYearsRemaining = withEnd.length && aum > 0
    ? withEnd.reduce((s, c) => {
        const yrs = Math.max(
          0,
          (new Date((c as any).end_date).getTime() - now.getTime()) / (365.25 * 24 * 3600 * 1000),
        )
        return s + yrs * num((c as any).total_value)
      }, 0) / withEnd.reduce((s, c) => s + num((c as any).total_value), 0)
    : null

  const in12 = new Date(now.getTime() + 365 * 24 * 3600 * 1000)
  const expiringWithin12Months = withEnd
    .filter(c => {
      const d = new Date((c as any).end_date)
      return d >= now && d <= in12
    })
    .reduce((s, c) => s + num((c as any).total_value), 0)

  return {
    aum,
    guaranteed,
    guaranteedPct,
    openPipeline,
    openCount: open.length,
    weightedPipeline,
    weightedPct,
    // From the commission ledger (commission on earned money); null when
    // the ledger has no row this year, never invented from paper value.
    feesYtd,
    // Deliberately null: requires an invoice/payment schedule.
    cashNext90: null,
    stages,
    holdings,
    concentration: {
      topThreePct,
      topLeague,
      weightedAvgYearsRemaining,
      expiringWithin12Months,
    },
  }
}

/** Compact money in the house format (utils/format): $284.6M / $13K / —. */
export function fmtMoney(v: number | null | undefined): string {
  return money(v)
}

export function fmtPct(v: number | null | undefined, digits = 0): string {
  return pct(v, digits)
}
